import { Chip, ChipProps, Stack, Tooltip, Typography } from '@mui/material'
import { ReactNode } from 'react'

type ChipItem = ChipProps & {
  label: NonNullable<ReactNode>
  tooltipLabel?: ReactNode
}

/**
 * A component that displays a list of chips, with a tooltip and indicator showing the hidden chips if there are more than maxChips
 * @param chips - The chips to display
 * @param maxChips - The maximum number of chips to display
 * @returns A stack of chips, with a tooltip showing the hidden chips if there are more than maxChips
 */
type DefaultChipListProps = {
  chips: ChipItem[]
  maxChips?: number
  totalCount?: number
}

export const DefaultChipList = ({ chips, maxChips = 3, totalCount }: DefaultChipListProps) => {
  const visibleChips = chips.slice(0, maxChips)
  const hiddenChips = chips.slice(maxChips).map((chip) => chip.tooltipLabel ?? chip.label)
  const hiddenChipCount = totalCount ? totalCount - visibleChips.length : hiddenChips.length
  const hiddenChipTitles = hiddenChips
    .map((chip) => {
      if (typeof chip === 'string') {
        return chip
      }
    })
    .join(', ')

  return (
    <Stack direction="row" spacing={2}>
      {visibleChips.map(({ tooltipLabel, ...chipProps }, index) => (
        <Tooltip key={`chip-${index}`} title={tooltipLabel ?? chipProps.label}>
          <Chip {...chipProps} />
        </Tooltip>
      ))}

      {hiddenChipCount > 0 && (
        <Tooltip title={hiddenChipTitles}>
          <Typography
            sx={{
              /* FIXME: use a supported typography variant */ fontSize: '0.625rem',
              backgroundColor: 'bg.neutral.strong',
              borderRadius: '6px',
              px: 1.5,
              m: 'auto !important',
              height: '22px',
              lineHeight: '16px',
              ml: '8px !important',
              alignContent: 'center',
            }}
            variant="small-regular"
            color="text.inverted"
          >
            +{hiddenChipCount}
          </Typography>
        </Tooltip>
      )}
    </Stack>
  )
}
